import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MediaPlayer from "@/components/MediaPlayer";
import { ChevronLeft, Music, Video, Search, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function BookDetail() {
  const { bookId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [book, setBook] = useState(null);
  const [media, setMedia] = useState([]);
  const [activeMedia, setActiveMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [progress, setProgress] = useState({ completed_media_ids: [], last_media_id: null, percent: 0, completed: 0, total: 0 });
  const playerRef = useRef(null);

  const filteredMedia = media.filter((m) => {
    if (!search.trim()) return true;
    return (m.title || "").toLowerCase().includes(search.trim().toLowerCase());
  });

  const loadProgress = async () => {
    if (isAdmin) return;
    try {
      const { data } = await api.get(`/progress/book/${bookId}`);
      setProgress(data);
    } catch (_) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: bk }, { data: m }] = await Promise.all([
          api.get(`/books/${bookId}`),
          api.get(`/books/${bookId}/media`),
        ]);
        setBook(bk);
        setMedia(m);
        if (!isAdmin) {
          try {
            const { data: pg } = await api.get(`/progress/book/${bookId}`);
            setProgress(pg);
            // Retomar de onde parou, se houver
            if (pg.last_media_id) {
              const found = m.find((x) => x.id === pg.last_media_id);
              if (found) { setActiveMedia(found); return; }
            }
          } catch (_) {}
        }
        if (m.length > 0) setActiveMedia(m[0]);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [bookId]);

  const pick = (m) => {
    setActiveMedia(m);
    // Em telas pequenas, leva o usuário até o player automaticamente.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches) {
      setTimeout(() => {
        playerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const reportProgress = ({ position, duration }) => {
    if (isAdmin || !activeMedia) return;
    api.post("/progress/current", { book_id: bookId, media_id: activeMedia.id, position, duration }).catch(() => {});
  };

  const reportCompleted = () => {
    if (isAdmin || !activeMedia) return;
    api.post("/progress/complete", { book_id: bookId, media_id: activeMedia.id })
      .then(({ data }) => setProgress(data))
      .catch(() => {});
  };

  const completedSet = new Set(progress.completed_media_ids || []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/"
          data-testid="back-to-books"
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#E30613] mb-4"
        >
          <ChevronLeft className="w-4 h-4" /> Todos os cursos
        </Link>

        {loading ? (
          <div className="text-neutral-500">Carregando...</div>
        ) : !book ? (
          <div className="text-neutral-500">Curso não encontrado.</div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E30613] mb-2">Curso</p>
              <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
                {book.title}
              </h1>
              {book.description && (
                <p className="text-neutral-500 mt-2 max-w-3xl">{book.description}</p>
              )}
              {!isAdmin && progress.total > 0 && (
                <div className="mt-4 max-w-md" data-testid="book-progress-bar">
                  <div className="flex justify-between items-center text-xs text-neutral-500 mb-1.5">
                    <span className="uppercase tracking-[0.2em]">Progresso</span>
                    <span className="font-semibold text-neutral-900">{progress.completed}/{progress.total} · {progress.percent}%</span>
                  </div>
                  <div className="h-2 bg-neutral-200 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#E30613] transition-all duration-500" style={{ width: `${progress.percent}%` }} />
                  </div>
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-[360px,1fr] gap-6">
              <aside className="border border-neutral-200 rounded-sm bg-white overflow-hidden self-start">
                <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-200 text-xs uppercase tracking-[0.2em] text-neutral-500">
                  Arquivos ({media.length})
                </div>
                {media.length > 0 && (
                  <div className="p-2 border-b border-neutral-100 bg-white">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                      <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-neutral-200 rounded-sm focus:outline-none focus:border-[#E30613] transition-colors"
                        data-testid="media-search"
                      />
                    </div>
                  </div>
                )}
                {media.length === 0 ? (
                  <div className="p-6 text-sm text-neutral-500">Nenhum arquivo disponível ainda.</div>
                ) : filteredMedia.length === 0 ? (
                  <div className="p-6 text-sm text-neutral-500">Nenhum arquivo encontrado.</div>
                ) : (
                  <ul data-testid="media-list" className="max-h-[65vh] overflow-y-auto">
                    {filteredMedia.map((m) => {
                      const isDone = completedSet.has(m.id);
                      const isActive = activeMedia?.id === m.id;
                      return (
                      <li key={m.id}>
                        <button
                          data-testid={`media-item-${m.id}`}
                          onClick={() => pick(m)}
                          className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-neutral-100 transition-colors ${
                            isActive ? "bg-black text-white" : "hover:bg-neutral-50"
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${isActive ? "bg-[#E30613] text-white" : isDone ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-700"}`}>
                            {isDone && !isActive ? <CheckCircle2 className="w-4 h-4" data-testid={`media-done-${m.id}`} /> : m.kind === "video" ? <Video className="w-4 h-4" /> : <Music className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{m.title}</div>
                            <div className={`text-xs mt-0.5 ${isActive ? "text-neutral-400" : "text-neutral-500"}`}>
                              {m.kind === "video" ? "Vídeo" : "Áudio"} • {(m.size / (1024 * 1024)).toFixed(1)} MB
                              {isDone && !isActive && <span className="ml-2 text-emerald-600 font-semibold">✓ Concluído</span>}
                            </div>
                          </div>
                        </button>
                      </li>
                      );
                    })}
                  </ul>
                )}
              </aside>

              <section className="space-y-6" ref={playerRef}>
                {activeMedia ? (
                  <MediaPlayer
                    media={activeMedia}
                    onProgress={reportProgress}
                    onCompleted={reportCompleted}
                  />
                ) : (
                  <div className="border border-dashed border-neutral-300 rounded-sm p-12 text-center text-neutral-500">
                    Os arquivos deste curso aparecerão aqui assim que forem adicionados.
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
